import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import EmptyContent from 'core/components/statusCard/EmptyContent'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import React, { useCallback } from 'react'
import { listLoadBalancers } from '../../networks/actions'
import { loadBalancersSelector } from '../../networks/selectors'
import CreateRepositoryModal from './CreateRepositoryModal'
import DeleteRepositoryDialog from './DeleteRepositoryDialog'
import { deleteRepository, listRepositories } from './actions'
import { repositorySelector } from './selector'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { formatTimestampToLocal } from '../../vms/helpers'

type ModelDataKey = DataKeys.Repository
type SelectorModel = ArrayElement<ReturnType<typeof loadBalancersSelector>>
type ActionParams = InferActionParams<typeof listLoadBalancers>
type Params = ActionParams & {}

const defaultParams: Params = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'OpenstackNetworks',
  listTablePrefs,
)

const searchTargets = ['name', 'keyName', 'keyType']

const searchFunctions = [
  {
    property: 'subnetDetails',
    searchFn: (value, searchString) => {
      return value.some(
        (subnet) =>
          subnet.name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          subnet.cidr.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()),
      )
    },
  },
]

const repositoryColumns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Git Repository URL',
    // width: 'medium',
  },
  {
    key: 'keyName',
    label: 'SSH Key Name',
  },
  {
    key: 'keyType',
    label: 'SSH Key Type',
  },

  {
    key: 'description',
    label: 'Description',
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function RepositoryListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)

  const { loading, reload } = useListAction(listRepositories)
  const data = useAppSelector(repositorySelector)

  const handleRefresh = useCallback(() => {
    reload(true)
  }, [])
  return (
    <>
      <CreateRepositoryModal addRoute={routeManager.routes.openstack.createRepository} />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.Repository}
        label="Git Repository"
        searchTargets={searchTargets}
        searchFunctions={searchFunctions}
        uniqueIdentifier="uid"
        showItemsCountInLabel={false}
        loading={loading}
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={data || []}
        columns={repositoryColumns}
        addUrl={routeManager.routes.openstack.createRepository.path()}
        addText="Add Git Repository"
        addCond={() => isAdmin()}
        disableAddButton={data?.length > 0}
        disabledAddButtonInfo="Support for adding multiple git repositories to the App Catalog is coming soon."
        deleteCond={() => isAdmin()}
        deleteDisabledInfo="Only administrators can delete git repositories."
        getParamsUpdater={getParamsUpdater}
        deleteAction={deleteRepository}
        DeleteDialogComponent={DeleteRepositoryDialog}
        multiSelection
        emptyContent={
          <EmptyContent
            title={'No Git Repository found with SSH Key'}
            subTitle={
              'SSH keys can be used to authenticate to private repositories where you store your terraform scripts.'
            }
          />
        }
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}
